<script lang="ts">
// @ts-nocheck
import { defineComponent, ref, computed, watch } from '@vue/composition-api';

import { wait } from '@/_core/utils/misc';

import NotificationsList from './components/NotificationsList.vue';

import * as notificationsModule from '@/store/global/notifications';
import { queryExecutor } from '@/_core/cqrs';
import { GetNotificationsOfWorkspaceQuery, GetAllNotificationsQuery } from '@/api/notifications/queries';
import {
    InitNotifications,
    HideDismissedNotifications,
    ShowAllNotifications,
    DismissNotificationOfUser,
    DismissAllNotificationsOfUser,
} from '@/store/global/notifications';
import { usePromise } from 'vue-composable';
import { goToExternalUrl } from '@/_core/utils/navigation';

import { TrackedPage } from '@/_core/tracking/trackedpages';
import { trackingService } from '@/_core/tracking/trackingService';

interface IProps {
    isOpen: boolean;
}

const trackPage = (current, isOpen) => {
    let subPage = null;
    if (current != null && isOpen) {
        if (current == 0) {
            subPage = TrackedPage.WorkspaceDashboardNotifications;
        }
        if (current == 1) {
            subPage = TrackedPage.WorkspaceDashboardDirectMessageNotifications;
        }
    }
    if (subPage != null) {
        trackingService.TrackSubpageView(subPage);
    }
};

const initData = async (isOpen: boolean): Promise<boolean> => {
    if (!isOpen) {
        return;
    }

    const notificationsStore = notificationsModule.useNotificationsStore();
    const notificationsQuery = initAppData.isInWsContext ? new GetNotificationsOfWorkspaceQuery() : new GetAllNotificationsQuery();

    const res = await queryExecutor.executeQuery(notificationsQuery);
    await notificationsStore.dispatch(new InitNotifications(res));

    return true;
};

const NotificationsPanel = defineComponent({
    name: 'NotificationsPanel',
    components: {
        NotificationsList,
    },
    props: { isOpen: Boolean },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props: IProps, ctx) {
        const tab = ref<number | null>(null);

        watch(
            tab,
            (current) => {
                trackPage(current, props.isOpen);
            },
            { immediate: true }
        );

        const store = notificationsModule.useNotificationsStore();
        const { loading: isRefresh, exec: init } = usePromise(() => initData(props.isOpen));

        watch(
            () => props.isOpen,
            async () => {
                if (props.isOpen) {
                    trackPage(tab.value, props.isOpen);
                    await wait(500);
                    await init();
                }
            },
            { immediate: true }
        );

        const loading = computed(() => {
            return isRefresh && !store.state.isInitialized;
        });

        const onClose = () => {
            ctx.emit('close');
        };

        const visibleNotifications = computed(() => {
            return store.state.visibleNotifications;
        });

        const allNotifications = computed(() => {
            return store.state.allNotifications;
        });

        const directMessages = computed(() => {
            return store.state.directMessages;
        });

        const isDirectMessagesActive = computed(() => {
            return tab.value === 1;
        });

        const onMarkAllAsRead = () => {
            store.dispatch(new DismissAllNotificationsOfUser(isDirectMessagesActive.value));
        };

        const hasUnDismissedNotification = computed(() => {
            return store.state.allNotifications.filter((item) => !item.isDismissed).length > 0;
        });

        const isMarkAllAsReadVisible = computed(() => {
            return store.state.allNotifications.length !== 0 && hasUnDismissedNotification.value;
        });

        const onShowUnread = () => {
            store.dispatch(new HideDismissedNotifications());
        };

        const isShowUnreadVisible = computed(() => {
            return store.state.allNotifications.length !== 0 && store.state.isDismissedNotificationsVisible;
        });

        const onShowAll = () => {
            store.dispatch(new ShowAllNotifications());
        };

        const isShowAllVisible = computed(() => {
            return store.state.allNotifications.length !== 0 && !isMarkAllAsReadVisible.value && !isShowUnreadVisible.value;
        });

        const onDismissNotification = (notificationId: string) => {
            store.dispatch(new DismissNotificationOfUser(notificationId));
        };

        const onOpenCard = ({ storyMapSlug, cardId, wsSubDomain }) => {
            if (initAppData.isInWsContext) {
                goToExternalUrl(`${initAppData.appUrls.appUrl}/storymap/${storyMapSlug}/${cardId}`, true);
            } else {
                const smurl = store.state.storymapUrlTemplate.replace('{0}', wsSubDomain).replace('{1}', storyMapSlug);
                const url = `${smurl}/${cardId}`;
                goToExternalUrl(url, true);
            }
        };

        const onOpenStoryMap = (storyMapSlug: string, wsSubDomain: string) => {
            let url = '';
            if (initAppData.isInWsContext) {
                url = `${initAppData.appUrls.appUrl}/storymap/${storyMapSlug}`;
            } else {
                url = store.state.storymapUrlTemplate.replace('{0}', wsSubDomain).replace('{1}', storyMapSlug);
            }
            goToExternalUrl(url, true);
        };

        const onOpenIdea = (ideaId: string, productSlug: string) => {
            const path = 'feedbackportal/ideas';

            const url = `${initAppData.appUrls.appUrl}/${productSlug}/${path}/${ideaId}`;
            goToExternalUrl(url, true);
        };

        const onOpenFeedback = (feedbackId: string, productSlug: string) => {
            const path = 'feedbackportal/insights';

            const url = `${initAppData.appUrls.appUrl}/${productSlug}/${path}/${feedbackId}`;
            goToExternalUrl(url, true);
        };

        const onOpenRoadmapCard = (roadmapCardId: string, roadmapId: string) => {
            const url = `${initAppData.appUrls.appUrl}/roadmap/${roadmapId}/${roadmapCardId}`;

            goToExternalUrl(url, true);
        };

        const onAcceptInvitation = (notificationSourceId: string) => {
            const url = `${initAppData.appUrls.rootUrl}/signup/acceptinvite?manualid=${notificationSourceId}`;
            goToExternalUrl(url, true);
        };

        return {
            tab,
            isDirectMessagesActive,
            onClose,
            loading,
            isRefresh,
            allNotifications,
            visibleNotifications,
            directMessages,
            onMarkAllAsRead,
            isMarkAllAsReadVisible,
            onShowUnread,
            isShowUnreadVisible,
            onShowAll,
            isShowAllVisible,
            onDismissNotification,
            onOpenCard,
            onOpenStoryMap,
            onOpenIdea,
            onOpenFeedback,
            onOpenRoadmapCard,
            onAcceptInvitation,
        };
    },
});

export default NotificationsPanel;
</script>

<template>
  <v-navigation-drawer
    v-model="isOpen"
    fixed
    right
    stateless
    width="410"
    disable-resize-watcher
    class="notifications-panel"
    :class="{ 'notifications-panel--desktop': $vuetify.breakpoint.lgAndUp }"
  >
    <div id="notifications-panel" class="v-navigation-drawer__content pa-4">
      <v-progress-linear
        :active="isRefresh"
        :indeterminate="true"
        absolute
        top
        color="primary lighten-2"
      />
      <div id="notifications-panel--header" class="d-flex align-center">
        <v-btn left icon @click="onClose">
          <v-icon color="primary">
            $next
          </v-icon>
        </v-btn>
        <span class="headline primary--text">
          Notifications
        </span>
        <v-spacer />
        <v-menu v-if="isMarkAllAsReadVisible || isShowUnreadVisible || isShowAllVisible" offset-y>
          <template #activator="{ on: menu }">
            <v-tooltip top>
              <template #activator="{ on: tooltip }">
                <v-btn
                  color="primary"
                  icon
                  class="action-button"
                  v-on="{ ...tooltip, ...menu }"
                >
                  <v-icon>
                    $falEllipsisV
                  </v-icon>
                </v-btn>
              </template>
              <span>Actions</span>
            </v-tooltip>
          </template>
          <v-card min-width="290" max-width="320">
            <v-list dense>
              <v-list-item v-if="isMarkAllAsReadVisible" @click="onMarkAllAsRead">
                <v-list-item-title>
                  mark all <span v-if="isDirectMessagesActive">direct messages </span>as read
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isShowUnreadVisible" @click="onShowUnread">
                <v-list-item-title>show unread</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isShowAllVisible" @click="onShowAll">
                <v-list-item-title>show all</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
      <v-divider />
      <div class="mx-n4">
        <v-tabs v-if="loading || visibleNotifications.length > 0" v-model="tab">
          <v-tab> All ({{ visibleNotifications.length }}) </v-tab>
          <v-tab> Direct messages ({{ directMessages.length }}) </v-tab>

          <v-tab-item>
            <v-skeleton-loader
              v-if="loading"
              boilerplate
              class="ma-2"
              height="250"
              type="list-item-avatar-three-line, card"
            />
            <NotificationsList
              :items="visibleNotifications"
              @onDismissNotification="onDismissNotification"
              @onOpenStoryMap="onOpenStoryMap"
              @onOpenCard="onOpenCard"
              @onOpenIdea="onOpenIdea"
              @onOpenFeedback="onOpenFeedback"
              @onOpenRoadmapCard="onOpenRoadmapCard"
              @onAcceptInvitation="onAcceptInvitation"
            />
          </v-tab-item>

          <v-tab-item>
            <v-skeleton-loader
              v-if="loading"
              boilerplate
              class="ma-2"
              height="250"
              type="list-item-avatar-three-line, card"
            />
            <NotificationsList
              v-else
              :items="directMessages"
              @onDismissNotification="onDismissNotification"
              @onOpenStoryMap="onOpenStoryMap"
              @onOpenCard="onOpenCard"
              @onOpenIdea="onOpenIdea"
              @onOpenFeedback="onOpenFeedback"
              @onOpenRoadmapCard="onOpenRoadmapCard"
              @onAcceptInvitation="onAcceptInvitation"
            />
          </v-tab-item>
        </v-tabs>
        <v-alert
          v-else-if="allNotifications.length === 0"
          class="ma-4"
          text
          type="info"
        >
          You haven't received any notifications so far.
        </v-alert>
        <v-alert
          v-else-if="allNotifications.length > 0"
          class="ma-4"
          text
          type="info"
        >
          There are no unread notifications.
        </v-alert>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.notifications-panel {
    &--desktop {
        top: 64px !important;
        height: calc(100% - 64px) !important;
    }

    &__header {
        transition: 0.2s all;
        background-color: #fff;

        &.sticked {
            width: 100%;
            position: absolute;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);
        }
    }
}
</style>
